/**
 * UpdatesPage — Devices ▸ Updates (May 2026 Figma).
 *
 * Layout:
 *   ┌──────────── UPDATE HEALTH ───────────┐ ┌────── UPDATE CONFIGURATION ──────┐
 *   │ ⭕ 75%  Indicates the firmware       │ │  [Automatic update: 01-04 AM] [⚪] │
 *   │         compliance score             │ │                                  │
 *   │         for all currently online …   │ │  ╔ Push updates to outdated …  ╗ │
 *   │         [ Fair ]                     │ │  ╚════════════════════════════╝ │
 *   └──────────────────────────────────────┘ └──────────────────────────────────┘
 *
 *   🔍 Updates   N TOTAL · N UP TO DATE · N OFFLINE · N ERRORS · N UPDATE AVAILABLE
 *   ─────────────────────────────────────────────────────────────────────────────
 *   DEVICE NAME ↑   TYPE   SERIAL   SITE   TIME   CURRENT VER   AVAILABLE VER   STATUS
 *
 * Status pill variants:
 *   Offline (gray) · Update available (yellow) · Updating… (blue, spinner) ·
 *   Up to date (green) · Error (red)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import deviceService from '../services/deviceService';

const DONUT_SIZE = 110;
const DEVICE_PAGE_SIZE = 200;
const ACTIVE_JOB_LIMIT = 50;
const REFRESH_INTERVAL_MS = 15000;
const DEFAULT_WINDOW_FROM = '1:00 AM';
const DEFAULT_WINDOW_TO = '4:00 AM';

/** Bucket a device into one of: offline | up_to_date | update_available | updating | error. */
function deriveUpdateStatus(d, latestVersion, updatingIds) {
  const id = d.deviceId ?? d.id;
  const online = String(d.currentStatus || d.status || '').toUpperCase();
  if (online === 'OFFLINE' || online === 'INACTIVE') return 'offline';
  if (updatingIds && updatingIds.has(id)) return 'updating';
  const current  = (d.firmwareVersion ?? d.currentFirmwareVersion ?? '').toString().trim();
  const available = (latestVersion ?? '').toString().trim();
  if (!available) return 'error';
  if (!current) return 'update_available';
  if (current === available) return 'up_to_date';
  return 'update_available';
}

const STATUS_META = {
  offline:           { key: 'offline',           label: 'Offline',           cls: 'cam-status cam-status-offline',   icon: 'fa-circle' },
  up_to_date:        { key: 'up_to_date',        label: 'Up to date',        cls: 'cam-status cam-status-active',    icon: 'fa-check' },
  update_available:  { key: 'update_available',  label: 'Update available',  cls: 'up-status-warn',                  icon: 'fa-exclamation' },
  updating:          { key: 'updating',          label: 'Updating…',         cls: 'up-status-progress',              icon: 'fa-circle-notch fa-spin' },
  error:             { key: 'error',             label: 'Error',             cls: 'cam-status cam-status-error',     icon: 'fa-times' },
};

function healthLabel(pct) {
  if (pct >= 85) return 'Good';
  if (pct >= 70) return 'Fair';
  return 'Poor';
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = d.getHours();
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ap = hh >= 12 ? 'pm' : 'am';
  const h12 = ((hh + 11) % 12) + 1;
  return `${mm}.${dd}.${yyyy} ${h12}:${mi} ${ap}`;
}

function apiTimeToDisplay(value) {
  const text = String(value || '').trim();
  const m = text.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
  if (!m) return '1:00 AM';
  const hh = Number(m[1]);
  const mm = m[2];
  const ap = hh >= 12 ? 'PM' : 'AM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${mm} ${ap}`;
}

function displayTimeToApi(value) {
  const m = String(value || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return '01:00';
  let hh = Number(m[1]) % 12;
  const mm = m[2];
  const ap = m[3].toUpperCase();
  if (ap === 'PM') hh += 12;
  return `${String(hh).padStart(2, '0')}:${mm}`;
}

async function loadAllDevices(orgId, siteId) {
  const devices = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (offset < total) {
    const response = await deviceService.listDevices(orgId, undefined, {
      siteId: siteId || undefined,
      limit: DEVICE_PAGE_SIZE,
      offset,
      sortBy: 'deviceName',
      sortOrder: 'asc',
    });
    const page = Array.isArray(response) ? response : (response?.content || response?.items || []);
    devices.push(...page);
    total = Number(response?.totalElements ?? devices.length);
    if (page.length === 0) break;
    offset += page.length;
  }

  return devices;
}

export default function UpdatesPage() {
  const { orgId, siteId, sites: tenantSites } = useTenant();

  const [devices, setDevices]       = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [sort, setSort]             = useState({ key: 'deviceName', asc: true });
  const [pushing, setPushing]       = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [updateHealth, setUpdateHealth] = useState(null);
  const refreshAll = () => setRefreshTick((t) => t + 1);
  const loadedTenantRef = useRef(null);

  const [autoOn, setAutoOn] = useState(false);
  const [scheduleFrom, setScheduleFrom] = useState(DEFAULT_WINDOW_FROM);
  const [scheduleTo, setScheduleTo] = useState(DEFAULT_WINDOW_TO);

  const sitesById = useMemo(() => {
    const m = new Map();
    (tenantSites || []).forEach((s) => {
      const id = s.siteId ?? s.id;
      m.set(id, s.siteName ?? s.name ?? `Site ${id}`);
    });
    return m;
  }, [tenantSites]);

  /* ---------- data loading ---------- */
  useEffect(() => {
    if (!orgId) { setDevices([]); setActiveJobs([]); return; }
    let cancelled = false;
    const tenantKey = `${orgId}:${siteId || 'all'}`;
    setLoading(loadedTenantRef.current !== tenantKey);
    setError(null);
    Promise.all([
      loadAllDevices(orgId, siteId),
      deviceService.listUpdateJobs(orgId, ACTIVE_JOB_LIMIT, 0, siteId).catch(() => null),
      deviceService.getAutomaticUpdateWindow(orgId).catch(() => null),
      deviceService.getUpdateHealth(orgId).catch(() => null),
    ])
      .then(([devicesRes, jobsRes, settingsRes, healthRes]) => {
        if (cancelled) return;
        setDevices(devicesRes);
        if (settingsRes) {
          // A disabled response may be an unsaved org default or a saved disabled setting;
          // in both cases the window values are suggestions until automatic updates are enabled.
          const enabled = Boolean(settingsRes.automaticUpdatesEnabled);
          setAutoOn(enabled);
          setScheduleFrom(settingsRes.updateWindowFrom ? apiTimeToDisplay(settingsRes.updateWindowFrom) : DEFAULT_WINDOW_FROM);
          setScheduleTo(settingsRes.updateWindowTo ? apiTimeToDisplay(settingsRes.updateWindowTo) : DEFAULT_WINDOW_TO);
        }
        if (healthRes) setUpdateHealth(healthRes);

        // Only track parent job status — per-device status comes from firmwareUpdateStatus
        // on the device listing, avoiding an N+1 fetch of job items at scale.
        const jobs = Array.isArray(jobsRes) ? jobsRes : (jobsRes?.content || jobsRes?.items || []);
        const running = jobs.filter((j) =>
          ['RUNNING', 'IN_PROGRESS', 'PENDING'].includes(String(j.status || '').toUpperCase()));
        if (!cancelled) setActiveJobs(running);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load updates');
      })
      .finally(() => {
        if (!cancelled) {
          loadedTenantRef.current = tenantKey;
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [orgId, siteId, refreshTick]);

  useEffect(() => {
    if (!orgId) return undefined;
    const intervalId = window.setInterval(
      () => setRefreshTick((tick) => tick + 1),
      REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(intervalId);
  }, [orgId, siteId]);

  const rows = useMemo(() => devices.map((d) => {
    const id = d.deviceId ?? d.id;
    // Use the per-device "available version" returned by the device listing API,
    // which is backed by the OTA-polled firmware cache in the backend.
    const availableVersion = d.availableFirmwareVersion || d.availableSoftwareVersion || '';
    // Prefer server-computed status; fall back to client-side derivation for older API responses
    const statusKey = d.firmwareUpdateStatus
      ? d.firmwareUpdateStatus.toLowerCase()
      : deriveUpdateStatus(d, availableVersion, null);
    return {
      id,
      raw: d,
      deviceName: d.deviceName ?? d.name ?? `Device ${id}`,
      type:       String(d.deviceType || d.type || 'Device')
                    .toLowerCase()
                    .replace('_', ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())
                    .replace(/^Dock(?:Station)?$/i, 'Dock')
                    .replace(/^Body Camera$/i, 'Body camera')
                    .replace(/^Bodycam$/i, 'Body camera'),
      serialNumber: d.serialNumber ?? d.serial ?? '',
      siteName:   d.siteName ?? sitesById.get(d.siteId) ?? '',
      time:       formatDate(d.lastUpdatedAt ?? d.updatedAt ?? d.lastSeenAt ?? d.registeredAt),
      currentVersion: d.firmwareVersion ?? d.currentFirmwareVersion ?? '',
      availableVersion,
      statusKey,
      status: STATUS_META[statusKey],
    };
  }), [devices, sitesById]);

  // summary comes from the /update-health API; fall back to zeros while loading
  const summary = useMemo(() => ({
    total:           updateHealth?.totalDevices      ?? 0,
    upToDate:        updateHealth?.upToDate          ?? 0,
    offline:         updateHealth?.offline           ?? 0,
    errors:          updateHealth?.error             ?? 0,
    updateAvailable: updateHealth?.updateAvailable   ?? 0,
    updating:        updateHealth?.updating          ?? 0,
  }), [updateHealth]);

  const healthPct = updateHealth?.healthPercentage ?? 0;

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? rows : rows.filter((r) =>
      [r.deviceName, r.type, r.serialNumber, r.siteName, r.currentVersion, r.availableVersion, r.status.label]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
    return [...base].sort((a, b) => {
      const get = (r) => {
        switch (sort.key) {
          case 'deviceName':       return r.deviceName || '';
          case 'type':             return r.type || '';
          case 'serialNumber':     return r.serialNumber || '';
          case 'siteName':         return r.siteName || '';
          case 'time':             return r.raw.lastUpdatedAt ?? r.raw.updatedAt ?? '';
          case 'currentVersion':   return r.currentVersion || '';
          case 'availableVersion': return r.availableVersion || '';
          case 'status':           return r.status.label;
          default: return '';
        }
      };
      return (sort.asc ? 1 : -1) * String(get(a)).localeCompare(String(get(b)));
    });
  }, [rows, search, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));
  const sortIcon = (key) =>
    sort.key === key ? (sort.asc ? 'fa-sort-up' : 'fa-sort-down') : '';

  /* ---------- push updates ---------- */
  const pushUpdates = async () => {
    if (!orgId) return;
    setPushing(true);
    try {
      await deviceService.pushOutdatedUpdates(orgId, siteId);
      refreshAll();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message ?? e.message ?? 'Failed to push updates');
    } finally {
      setPushing(false);
    }
  };

  /* ---------- automatic update window scheduling ---------- */
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({ from: scheduleFrom, to: scheduleTo });
  const persistWindowSettings = async (enabled, fromDisplay, toDisplay) => {
    if (!orgId) return;
    const saved = await deviceService.saveAutomaticUpdateWindow({
      automaticUpdatesEnabled: enabled,
      updateWindowFrom: displayTimeToApi(fromDisplay),
      updateWindowTo: displayTimeToApi(toDisplay),
    }, orgId);
    setAutoOn(Boolean(saved.automaticUpdatesEnabled));
    setScheduleFrom(apiTimeToDisplay(saved.updateWindowFrom));
    setScheduleTo(apiTimeToDisplay(saved.updateWindowTo));
  };

  const openSchedule = () => {
    setScheduleDraft({ from: scheduleFrom, to: scheduleTo });
    setScheduleOpen(true);
  };
  const saveSchedule = async () => {
    try {
      await persistWindowSettings(autoOn, scheduleDraft.from, scheduleDraft.to);
      setScheduleOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message ?? e.message ?? 'Failed to save automatic update window');
    }
  };

  const toggleAutomaticUpdates = async () => {
    const next = !autoOn;
    setAutoOn(next);
    try {
      await persistWindowSettings(next, scheduleFrom, scheduleTo);
    } catch (e) {
      setAutoOn(!next);
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message ?? e.message ?? 'Failed to save automatic update setting');
    }
  };

  const autoWindowLabel = `${scheduleFrom} - ${scheduleTo}`;

  /* ---------- render ---------- */
  return (
    <>
      {/* Top: UPDATE HEALTH + UPDATE CONFIGURATION cards */}
      <div className="up-top-grid">
        <section className="up-card up-health">
          <div
            className="up-donut"
            style={{
              width: DONUT_SIZE,
              height: DONUT_SIZE,
              '--pct': `${healthPct}%`,
            }}
            aria-label={`${healthPct}% firmware compliance`}
          >
            <span className="up-donut-text">{healthPct}%</span>
          </div>
          <div className="up-health-body">
            <h3>UPDATE HEALTH</h3>
            <p>Indicates the firmware compliance score for all currently online devices</p>
            <span className={`up-health-pill up-health-${(updateHealth?.healthLabel ?? 'Poor').toLowerCase().replace(/\s+/g, '-')}`}>
              {updateHealth?.healthLabel ?? 'Poor'}
            </span>
          </div>
        </section>

        <section className="up-card up-config">
          <div className="up-config-header">
            <h3>UPDATE CONFIGURATION</h3>
            <div className="up-config-actions">
              <span className="up-auto-chip" title="Automatic update maintenance window">
                <i className={`fas ${autoOn ? 'fa-check' : 'fa-pause'}`}></i>{' '}
                {autoOn ? `Automatic update: ${autoWindowLabel}` : 'Automatic updates disabled'}
              </span>
              <span className="up-auto-label">Automatic updates</span>
              <i
                className="far fa-question-circle up-help"
                title="When on, firmware updates are pushed automatically inside the configured maintenance window."
              />
              <button
                type="button"
                className={`up-toggle ${autoOn ? 'on' : ''}`}
                onClick={toggleAutomaticUpdates}
                aria-label="Toggle automatic updates"
                role="switch"
                aria-checked={autoOn}
              >
                <span className="up-toggle-knob" />
              </button>
              <button
                type="button"
                className="cm-icon-btn cm-icon-btn-circle"
                title="Schedule automatic update window"
                aria-label="Schedule automatic update window"
                onClick={openSchedule}
              >
                <i className="fas fa-cog"></i>
              </button>
            </div>
          </div>
          <button
            type="button"
            className="up-push-btn"
            onClick={pushUpdates}
            disabled={pushing || activeJobs.length > 0}
          >
            {pushing || activeJobs.length > 0 ? 'Updating devices...' : 'Push updates to outdated devices'}
          </button>
        </section>
      </div>

      {/* Toolbar */}
      <div className="sp-toolbar up-toolbar-spaced">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search updates"
            onClick={() => setShowSearch((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Updates</h1>
            <div className="up-summary up-summary-wide">
              <span className="up-summary-item"><strong>{summary.total}</strong> TOTAL</span>
              <span className="up-summary-item"><span className="up-dot up-dot-active" /> {summary.upToDate} UP TO DATE</span>
              <span className="up-summary-item"><span className="up-dot up-dot-offline" /> {summary.offline} OFFLINE</span>
              <span className="up-summary-item up-summary-err"><i className="fas fa-times-circle"></i> {summary.errors} ERRORS</span>
              <span className="up-summary-item up-summary-warn"><i className="fas fa-exclamation-circle"></i> {summary.updateAvailable} UPDATE AVAILABLE</span>
            </div>
            {showSearch && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Search devices..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="cameras-table-card">
        <table className="data-table cameras-table">
          <thead>
            <tr>
              <th>
                <button className="data-table-sort" onClick={() => toggleSort('deviceName')}>
                  DEVICE NAME {sortIcon('deviceName') && <i className={`fas ${sortIcon('deviceName')}`}></i>}
                </button>
              </th>
              <th><button className="data-table-sort" onClick={() => toggleSort('type')}>TYPE</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('serialNumber')}>SERIAL NUMBER</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('siteName')}>SITE</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('time')}>TIME</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('currentVersion')}>CURRENT VERSION</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('availableVersion')}>AVAILABLE VERSION</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('status')}>STATUS</button></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                <i className="fas fa-spinner fa-spin" /> Loading devices…
              </td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: '#fca5a5' }}>{error}</td></tr>
            )}
            {!loading && !error && filteredSorted.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                No devices to show.
              </td></tr>
            )}
            {!loading && !error && filteredSorted.map((r) => (
              <tr key={r.id}>
                <td>{r.deviceName}</td>
                <td>{r.type}</td>
                <td>{r.serialNumber || <span className="up-muted">—</span>}</td>
                <td>{r.siteName || <span className="up-muted">—</span>}</td>
                <td>{r.time}</td>
                <td>{r.currentVersion || <span className="up-muted">-</span>}</td>
                <td>{r.availableVersion || <span className="up-muted">-</span>}</td>
                <td>
                  <span className={r.status.cls}>
                    <i className={`fas ${r.status.icon}`}></i>
                    {r.status.label}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {scheduleOpen && (
        <ScheduleWindowModal
          from={scheduleDraft.from}
          to={scheduleDraft.to}
          onChange={(patch) => setScheduleDraft((d) => ({ ...d, ...patch }))}
          onCancel={() => setScheduleOpen(false)}
          onSave={saveSchedule}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Automatic update window modal                                             */
/* ------------------------------------------------------------------------ */

const HOUR_OPTIONS = (() => {
  // 12-hour clock, 30-minute granularity, AM then PM.
  const out = [];
  const labels = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
  for (const ap of ['AM', 'PM']) {
    for (const h of labels) {
      for (const m of ['00', '30']) {
        out.push(`${h}:${m} ${ap}`);
      }
    }
  }
  return out;
})();

function ScheduleWindowModal({ from, to, onChange, onCancel, onSave }) {
  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Automatic update window"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="os-modal os-modal-single up-sched-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header up-sched-header">
          <h2>Automatic update window</h2>
          <p>Schedule firmware updates for all devices at all sites</p>
        </header>

        <div className="os-modal-body up-sched-body">
          <div className="up-sched-grid">
            <div className="os-field">
              <label className="os-label"><span>Update window from</span></label>
              <select
                className="os-input os-select"
                value={from}
                onChange={(e) => onChange({ from: e.target.value })}
              >
                {HOUR_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="os-field">
              <label className="os-label"><span>Update window end</span></label>
              <select
                className="os-input os-select"
                value={to}
                onChange={(e) => onChange({ to: e.target.value })}
              >
                {HOUR_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-primary-pill" onClick={onSave}>Save</button>
        </footer>
      </div>
    </div>
  );
}
